import * as Yup from "yup";
import { zxcvbn, zxcvbnOptions } from "@zxcvbn-ts/core";
import * as zxcvbnCommonPackage from "@zxcvbn-ts/language-common";
import * as zxcvbnEnPackage from "@zxcvbn-ts/language-en";

export const PASSWORD_MIN_LENGTH = 12;

// Legacy policy (used when strong validation is disabled at the site level).
export const LEGACY_PASSWORD_MIN_LENGTH = 8;

// zxcvbn score is 0 (worst) - 4 (best). Require "strong" (3+).
export const PASSWORD_MIN_SCORE = 3;

type Translator = (key: string, opts?: any) => string;

let configured = false;

// zxcvbn dictionaries/graphs are loaded once. They power detection of common
// passwords, names, l33t-speak, keyboard/sequence patterns and dates.
function ensureConfigured(): void {
  if (configured) return;
  zxcvbnOptions.setOptions({
    dictionary: {
      ...zxcvbnCommonPackage.dictionary,
      ...zxcvbnEnPackage.dictionary,
    },
    graphs: zxcvbnCommonPackage.adjacencyGraphs,
    translations: zxcvbnEnPackage.translations,
  });
  configured = true;
}

function toUserInputs(terms: Array<string | undefined | null>): string[] {
  return terms
    .filter((t): t is string => Boolean(t))
    .map((t) => String(t).trim())
    .filter((t) => t.length > 0);
}

export interface PasswordStrength {
  score: number; // 0 (worst) - 4 (best)
  lengthOk: boolean;
  /** Short localized hint from zxcvbn (English language pack), if any. */
  warning?: string;
  suggestions: string[];
}

/** Strength evaluation backed by zxcvbn. `personalTerms` (email, name, phone…)
 *  are passed as user inputs so passwords derived from them score low. */
export function estimateStrength(
  password: string,
  personalTerms: Array<string | undefined | null> = []
): PasswordStrength {
  const lengthOk = password.length >= PASSWORD_MIN_LENGTH;

  if (!password) {
    return { score: 0, lengthOk: false, suggestions: [] };
  }

  ensureConfigured();
  const result = zxcvbn(password, toUserInputs(personalTerms));

  return {
    score: result.score,
    lengthOk,
    warning: result.feedback.warning || undefined,
    suggestions: result.feedback.suggestions || [],
  };
}

export interface StrongPasswordOptions {
  /** Sibling form fields whose values must not appear inside the password. */
  personalFields?: string[];
  /** Extra static terms (e.g. brand name) to forbid inside the password. */
  forbiddenTerms?: string[];
}

/** Reusable Yup schema enforcing the strong password policy. Pass the i18n
 *  translator so messages stay localized; reads sibling fields via `this.parent`
 *  and feeds them to zxcvbn as user inputs. */
export function yupStrongPassword(
  t: Translator,
  options: StrongPasswordOptions = {}
): Yup.StringSchema {
  const { personalFields = ["email"], forbiddenTerms = [] } = options;

  const collectTerms = (parent: unknown): Array<string | undefined | null> => {
    const record = (parent as Record<string, unknown>) || {};
    const siblingTerms = personalFields.map(
      (field) => record[field] as string | undefined
    );
    return [...siblingTerms, ...forbiddenTerms];
  };

  return Yup.string()
    .required(t("password_policy.required"))
    .min(
      PASSWORD_MIN_LENGTH,
      t("password_policy.min", { min: PASSWORD_MIN_LENGTH })
    )
    .matches(/[a-z]/, t("password_policy.lowercase"))
    .matches(/[A-Z]/, t("password_policy.uppercase"))
    .matches(/[0-9]/, t("password_policy.number"))
    .matches(/[^A-Za-z0-9]/, t("password_policy.special"))
    .test(
      "strength",
      t("password_policy.too_weak"),
      function strengthTest(value) {
        const { score } = estimateStrength(
          value || "",
          collectTerms(this.parent)
        );
        return score >= PASSWORD_MIN_SCORE;
      }
    );
}

/** Legacy Yup schema: 8 chars, at least one lowercase, uppercase and digit.
 *  Used when the site-level strong password flag is off or unavailable. */
export function yupLegacyPassword(t: Translator): Yup.StringSchema {
  return Yup.string()
    .required(t("password_policy.required"))
    .min(
      LEGACY_PASSWORD_MIN_LENGTH,
      t("password_policy.min", { min: LEGACY_PASSWORD_MIN_LENGTH })
    )
    .matches(/[a-z]/, t("password_policy.lowercase"))
    .matches(/[A-Z]/, t("password_policy.uppercase"))
    .matches(/[0-9]/, t("password_policy.number"));
}

/** Picks the password schema based on the site-level flag. Defaults to the
 *  legacy policy so a missing/unloaded setting never blocks registration. */
export function yupPassword(
  t: Translator,
  options: StrongPasswordOptions & { strong?: boolean } = {}
): Yup.StringSchema {
  const { strong = false, ...strongOptions } = options;
  return strong ? yupStrongPassword(t, strongOptions) : yupLegacyPassword(t);
}
